import { z } from 'zod';

/**
 * Phase 4: CODEOWNERS-style auto-assignment. Backed by
 * `project_codeowners` (migration 050). When a review is opened, we
 * resolve the matching rules and seed the reviewer list with the
 * groups/users they reference.
 *
 * Each rule's `reviewers` is a list of user UUIDs and/or
 * `group:<group_uuid>` strings. The codeowners-service expands
 * groups at lookup time so admins can manage membership in one
 * place and have it propagate everywhere.
 *
 * Patterns supported in v1:
 *
 *   * `all_flows` — every flow in the project (pattern field unused)
 *   * `folder`    — pattern is a folder UUID (depth-1 match)
 *   * `flow_name` — pattern is a prefix; matches flow.name LIKE pattern%
 */

export const CodeownerPatternKindSchema = z.enum(['all_flows', 'folder', 'flow_name']);
export type CodeownerPatternKind = z.infer<typeof CodeownerPatternKindSchema>;

/**
 * A reviewer reference inside a codeowner rule. Either a raw user
 * UUID or a `group:<group_id>` reference. The schema validates the
 * stringy shape; expansion to concrete users happens server-side.
 */
export const CodeownerReviewerRefSchema = z
  .string()
  .refine((s) => /^[0-9a-f-]{36}$/i.test(s) || /^group:[0-9a-f-]{36}$/i.test(s), {
    message: 'Reviewer ref must be a UUID or "group:<uuid>"',
  });
export type CodeownerReviewerRef = z.infer<typeof CodeownerReviewerRefSchema>;

export const ProjectCodeownerSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  patternKind: CodeownerPatternKindSchema,
  /** Free-text pattern body — empty for `all_flows`, UUID for folder, prefix for flow_name. */
  pattern: z.string().nullable(),
  reviewers: z.array(CodeownerReviewerRefSchema),
  required: z.boolean(),
  priority: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectCodeowner = z.infer<typeof ProjectCodeownerSchema>;

export const CreateProjectCodeownerBodySchema = z.object({
  patternKind: CodeownerPatternKindSchema,
  pattern: z.string().max(200).optional(),
  reviewers: z.array(CodeownerReviewerRefSchema).min(1).max(20),
  required: z.boolean().default(true),
  priority: z.number().int().min(0).max(1000).default(0),
});
export type CreateProjectCodeownerBody = z.infer<typeof CreateProjectCodeownerBodySchema>;

export const UpdateProjectCodeownerBodySchema = z.object({
  patternKind: CodeownerPatternKindSchema.optional(),
  pattern: z.string().max(200).optional(),
  reviewers: z.array(CodeownerReviewerRefSchema).min(1).max(20).optional(),
  required: z.boolean().optional(),
  priority: z.number().int().min(0).max(1000).optional(),
});
export type UpdateProjectCodeownerBody = z.infer<typeof UpdateProjectCodeownerBodySchema>;

export const ListProjectCodeownersResponseSchema = z.object({
  codeowners: z.array(ProjectCodeownerSchema),
});
export type ListProjectCodeownersResponse = z.infer<typeof ListProjectCodeownersResponseSchema>;
